/**
 * Analysis Renderer
 * Renders analysis results to the UI
 *
 * @module AnalysisRenderer
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation
} from '../types/ii-agent-types';
import { safeFeatherReplace } from '../utils/html-utils';

/**
 * Interface for analysis renderer
 */
export interface IAnalysisRenderer {
    displayStats(stats: any[] | any): void;
    displayInfrastructureReport(report: InfrastructureReport): void;
    displayAnomalies(anomalies: Anomaly[]): void;
    displaySecurityFindings(findings: SecurityFinding[]): void;
    displayCostRecommendations(recommendations: CostRecommendation[]): void;
}

/**
 * Renderer for analysis results
 */
export class AnalysisRenderer implements IAnalysisRenderer {

    /**
     * Display stats
     */
    displayStats(stats: any[] | any): void {
        console.log('displayStats called with:', stats);
        console.log('Stats type:', typeof stats, 'isArray:', Array.isArray(stats));

        const statsArray = Array.isArray(stats) ? stats : [];
        let statsMap: Record<string, number> = {};

        if (statsArray.length > 0) {
            if (Array.isArray(statsArray[0])) {
                // Format: Array of tuples
                statsMap = Object.fromEntries(statsArray);
            } else if (typeof statsArray[0] === 'object') {
                // Format: Array of objects
                statsArray.forEach((obj: any) => {
                    Object.assign(statsMap, obj);
                });
            }
        }

        const totalSessionsEl = document.getElementById('stat-total-sessions');
        const totalFindingsEl = document.getElementById('stat-total-findings');
        const totalRecommendationsEl = document.getElementById('stat-total-recommendations');

        if (totalSessionsEl) totalSessionsEl.textContent = String(statsMap.total_sessions || 0);
        if (totalFindingsEl) totalFindingsEl.textContent = String(statsMap.total_findings || 0);
        if (totalRecommendationsEl) totalRecommendationsEl.textContent = String(statsMap.total_recommendations || 0);

        console.log('Stats displayed successfully');
    }

    /**
     * Display infrastructure report
     */
    displayInfrastructureReport(report: InfrastructureReport): void {
        const container = document.getElementById('infrastructure-results') as HTMLElement;
        if (!container) {
            console.warn('Infrastructure results container not found');
            return;
        }

        const deviceBreakdown = Object.entries(report.device_breakdown || [])
            .map(([type, count]) => `<li>${type}: ${count}</li>`)
            .join('');

        const osDistribution = Object.entries(report.os_distribution || [])
            .map(([os, count]) => `<li>${os}: ${count}</li>`)
            .join('');

        const cloudProviders = Object.entries(report.cloud_provider_breakdown || [])
            .map(([provider, count]) => `<li>${provider}: ${count}</li>`)
            .join('');

        const healthColor = report.health_score >= 80 ? 'text-green-600' :
                           report.health_score >= 60 ? 'text-yellow-600' : 'text-red-600';

        container.innerHTML = `
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div class="stat-card">
                    <div class="text-sm text-gray-600 mb-1">Total Devices</div>
                    <div class="text-3xl font-bold">${report.total_devices}</div>
                </div>
                <div class="stat-card">
                    <div class="text-sm text-gray-600 mb-1">Total Applications</div>
                    <div class="text-3xl font-bold">${report.total_applications}</div>
                </div>
                <div class="stat-card">
                    <div class="text-sm text-gray-600 mb-1">Health Score</div>
                    <div class="text-3xl font-bold ${healthColor}">${report.health_score.toFixed(1)}%</div>
                </div>
                <div class="stat-card">
                    <div class="text-sm text-gray-600 mb-1">Generated At</div>
                    <div class="text-sm">${new Date(Number(report.generated_at) / 1000000).toLocaleString()}</div>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="stat-card">
                    <h4 class="font-semibold mb-2 flex items-center gap-2">
                        <i data-feather="server"></i> Device Breakdown
                    </h4>
                    <ul class="text-sm space-y-1">${deviceBreakdown || '<li>None</li>'}</ul>
                </div>
                <div class="stat-card">
                    <h4 class="font-semibold mb-2 flex items-center gap-2">
                        <i data-feather="cpu"></i> OS Distribution
                    </h4>
                    <ul class="text-sm space-y-1">${osDistribution || '<li>None</li>'}</ul>
                </div>
                <div class="stat-card">
                    <h4 class="font-semibold mb-2 flex items-center gap-2">
                        <i data-feather="cloud"></i> Cloud Providers
                    </h4>
                    <ul class="text-sm space-y-1">${cloudProviders || '<li>None</li>'}</ul>
                </div>
            </div>
        `;

        safeFeatherReplace();
        container.classList.remove('hidden');
    }

    /**
     * Display anomalies
     */
    displayAnomalies(anomalies: Anomaly[]): void {
        const container = document.getElementById('anomalies-results') as HTMLElement;
        if (!container) {
            console.warn('Anomalies results container not found');
            return;
        }

        if (anomalies.length === 0) {
            container.innerHTML = '<div class="text-center text-gray-600 py-8">No anomalies detected!</div>';
            container.classList.remove('hidden');
            return;
        }

        const anomaliesHTML = anomalies.map((anomaly) => {
            const scoreColor = anomaly.anomaly_score >= 0.7 ? 'text-red-600' :
                             anomaly.anomaly_score >= 0.5 ? 'text-yellow-600' : 'text-blue-600';

            return `
                <div class="device-card">
                    <div class="flex justify-between items-start mb-3">
                        <div>
                            <h4 class="font-semibold text-lg">${anomaly.device_name}</h4>
                            <div class="text-sm text-gray-600">${anomaly.device_id}</div>
                        </div>
                        <div class="text-right">
                            <div class="${scoreColor} font-bold text-xl">${(anomaly.anomaly_score * 100).toFixed(1)}%</div>
                            <div class="text-xs text-gray-600">Anomaly Score</div>
                        </div>
                    </div>
                    <div class="text-sm">
                        <div class="mb-2">
                            <strong>Peer Similarity:</strong> ${(anomaly.average_similarity * 100).toFixed(1)}%
                            (${anomaly.peer_count} peers analyzed)
                        </div>
                        <div>
                            <strong>Reasons:</strong>
                            <ul class="list-disc list-inside mt-1 space-y-1">
                                ${anomaly.reasons.map(r => `<li>${r}</li>`).join('')}
                            </ul>
                        </div>
                    </div>
                </div>
            `;
        }).join('');

        container.innerHTML = `
            <div class="mb-4">
                <h3 class="text-lg font-semibold">Found ${anomalies.length} Anomalies</h3>
            </div>
            <div class="grid grid-cols-1 gap-4">
                ${anomaliesHTML}
            </div>
        `;

        container.classList.remove('hidden');
    }

    /**
     * Display security findings
     */
    displaySecurityFindings(findings: SecurityFinding[]): void {
        const container = document.getElementById('security-results') as HTMLElement;
        if (!container) {
            console.warn('Security results container not found');
            return;
        }

        if (findings.length === 0) {
            container.innerHTML = '<div class="text-center text-green-600 py-8"><i data-feather="check-circle"></i> No security violations found!</div>';
            container.classList.remove('hidden');
            safeFeatherReplace();
            return;
        }

        const findingsHTML = findings.map(finding => {
            const severity = Object.keys(finding.severity)[0];
            const severityColors: Record<string, string> = {
                Critical: 'bg-red-100 text-red-800 border-red-300',
                High: 'bg-orange-100 text-orange-800 border-orange-300',
                Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                Low: 'bg-blue-100 text-blue-800 border-blue-300',
                Info: 'bg-gray-100 text-gray-800 border-gray-300'
            };

            return `
                <div class="device-card border-l-4 ${severityColors[severity] || severityColors.Info}">
                    <div class="flex justify-between items-start mb-2">
                        <h4 class="font-semibold">${finding.title}</h4>
                        <span class="px-2 py-1 text-xs rounded ${severityColors[severity] || severityColors.Info}">${severity}</span>
                    </div>
                    <p class="text-sm text-gray-700 mb-2">${finding.description}</p>
                    <div class="text-xs text-gray-600">
                        <strong>Affected:</strong> ${finding.affected_resources.join(', ')}
                    </div>
                </div>
            `;
        }).join('');

        container.innerHTML = `
            <div class="mb-4">
                <h3 class="text-lg font-semibold text-red-600">Found ${findings.length} Security Findings</h3>
            </div>
            <div class="grid grid-cols-1 gap-4">
                ${findingsHTML}
            </div>
        `;

        container.classList.remove('hidden');
    }

    /**
     * Display cost recommendations
     */
    displayCostRecommendations(recommendations: CostRecommendation[]): void {
        const container = document.getElementById('cost-results') as HTMLElement;
        if (!container) {
            console.warn('Cost results container not found');
            return;
        }

        if (recommendations.length === 0) {
            container.innerHTML = '<div class="text-center text-green-600 py-8">No cost optimization opportunities found at this time!</div>';
            container.classList.remove('hidden');
            return;
        }

        const recsHTML = recommendations.map(rec => {
            const priority = Object.keys(rec.priority)[0];
            const priorityColors: Record<string, string> = {
                Critical: 'bg-red-100 text-red-800',
                High: 'bg-orange-100 text-orange-800',
                Medium: 'bg-yellow-100 text-yellow-800',
                Low: 'bg-blue-100 text-blue-800'
            };

            return `
                <div class="device-card">
                    <div class="flex justify-between items-start mb-3">
                        <h4 class="font-semibold">${rec.title}</h4>
                        <span class="px-2 py-1 text-xs rounded ${priorityColors[priority] || priorityColors.Low}">${priority}</span>
                    </div>
                    <p class="text-sm text-gray-700 mb-3">${rec.description}</p>
                    <div class="bg-green-50 border border-green-200 rounded p-2 mb-3">
                        <div class="text-sm font-semibold text-green-800">${rec.estimated_impact}</div>
                    </div>
                    <div class="text-sm">
                        <strong>Action Items:</strong>
                        <ul class="list-disc list-inside mt-1 space-y-1">
                            ${rec.action_items.map(item => `<li>${item}</li>`).join('')}
                        </ul>
                    </div>
                </div>
            `;
        }).join('');

        container.innerHTML = `
            <div class="mb-4">
                <h3 class="text-lg font-semibold text-green-600">Found ${recommendations.length} Cost Optimization Opportunities</h3>
            </div>
            <div class="grid grid-cols-1 gap-4">
                ${recsHTML}
            </div>
        `;

        container.classList.remove('hidden');
    }
}
